import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useFreshIdToken from '../auth/useFreshIdToken';
import { fetchSimRefund } from './refundApi';

/**
 * Refund state of one SIM for the signed-in buyer.
 *
 * status: 'loading' | 'anonymous' | 'ready' | 'forbidden' | 'error'
 *   anonymous - not signed in, so eligibility is unknown until they log in
 *   forbidden - signed in, but not the buyer (e.g. an eSIM shared with them)
 * refund is the existing request, if any; eligibility is set only when there
 * is none.
 */
export default function useSimRefund(simId) {
    const { isAuthenticated, isLoading } = useAuth0();
    const getToken = useFreshIdToken();
    const [state, setState] = useState({ status: 'loading', refund: null, eligibility: null });

    const load = useCallback(async () => {
        if (isLoading) return;
        if (!isAuthenticated) {
            setState({ status: 'anonymous', refund: null, eligibility: null });
            return;
        }

        setState((prev) => ({ ...prev, status: 'loading' }));
        try {
            const token = await getToken();
            const body = await fetchSimRefund(simId, token);
            setState({ status: 'ready', refund: body.refund, eligibility: body.eligibility });
        } catch (error) {
            setState({
                status: error.status === 403 ? 'forbidden' : 'error',
                refund: null,
                eligibility: null,
            });
        }
    }, [simId, isAuthenticated, isLoading, getToken]);

    useEffect(() => {
        load();
    }, [load]);

    return { ...state, reload: load };
}
